import { useCallback, useEffect, useId, useRef, useState } from 'react';

// Popups (menús y paneles flotantes): uno abierto a la vez. Abrir uno cierra los demás, y se cierra al hacer clic
// fuera de él o con Escape (el foco vuelve al botón que lo abrió).
const openers = new Set<(id: string) => void>();

export function usePopover<T extends HTMLElement = HTMLDivElement>() {
  const id = useId();
  const [open, setOpenState] = useState(false);
  const ref = useRef<T>(null);

  useEffect(() => {
    const onOtherOpened = (other: string) => {
      if (other !== id) setOpenState(false);
    };
    openers.add(onOtherOpened);
    return () => {
      openers.delete(onOtherOpened);
    };
  }, [id]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpenState(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setOpenState(false);
      ref.current?.querySelector<HTMLElement>('[aria-expanded]')?.focus();
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const setOpen = useCallback(
    (value: boolean) => {
      if (value) for (const notify of openers) notify(id);
      setOpenState(value);
    },
    [id],
  );

  return { open, setOpen, toggle: () => setOpen(!open), ref };
}
